import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Copy, Info } from 'lucide-react';
import { SearchInput } from '../../ui/SearchInput';
import { shortenUuid } from '../../../utils/id';
import { useSessionInfo, useSessionsList } from '../../../hooks/useSessionData';
import { useCoachPreferences } from '../../../hooks/useCoachPreferences';

interface SessionInfo {
  id?: string; // UUID for short pill + copy
  sessionId?: string; // legacy/import label (not shown prominently)
  sessionName?: string;
  location?: string;
  sessionDate: string;
  startTime?: string;
  boatType?: string;
  crewCount?: number;
}

interface CompactHeaderActions {
  onShare: () => void;
  onExport: () => void;
  onSettings: () => void;
}

interface CompactHeaderProps {
  sessionInfo: SessionInfo;
  actions: CompactHeaderActions;
  onSelectSession?: (sessionId: string) => void;
  hideSessionSwitcher?: boolean;
  sectionLinks?: Array<{ label: string; hash: string }>;
  onSelectSection?: (hash: string) => void;
  sectionLinksHint?: string;
}

const CompactHeader: React.FC<CompactHeaderProps> = ({
  sessionInfo,
  actions: _actions,
  onSelectSession,
  hideSessionSwitcher,
  sectionLinks = [],
  onSelectSection,
  sectionLinksHint = 'Starter Dashboard combines charts, cards, and GPS map. Use Create New to build your own dashboard.',
}) => {
  const { data: sessions = [] } = useSessionsList();
  const resolvedSessionId = sessionInfo.id || null;
  const { data: resolvedSessionMeta } = useSessionInfo(resolvedSessionId);
  const { preferences: coachPreferences } = useCoachPreferences();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const switcherRef = useRef<HTMLDivElement | null>(null);

  // Filter sessions based on showDemoContent preference; include selected session if archived
  const filteredSessions = useMemo(() => {
    let list = coachPreferences.showDemoContent
      ? sessions
      : sessions.filter((session) => !session.isDemo);
    if (
      resolvedSessionId &&
      resolvedSessionMeta &&
      resolvedSessionMeta.id === resolvedSessionId &&
      !list.some((s) => s.id === resolvedSessionId)
    ) {
      list = [resolvedSessionMeta, ...list];
    }
    return list;
  }, [sessions, coachPreferences.showDemoContent, resolvedSessionId, resolvedSessionMeta]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!open) return;
      const target = e.target as Node;
      if (switcherRef.current && !switcherRef.current.contains(target)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return filteredSessions.slice(0, 8);
    return filteredSessions
      .filter((s) => {
        const label =
          `${s.sessionName || s.sessionId || ''} ${s.crewName || ''} ${s.sessionDate || ''}`.toLowerCase();
        return label.includes(q);
      })
      .slice(0, 8);
  }, [filteredSessions, query]);

  const labelFor = (s: {
    sessionName?: string;
    crewName?: string;
    sessionDate?: string;
    startTime?: string;
    id: string;
    sessionId: string;
  }) => {
    const name = s.sessionName || s.sessionId;
    const crew = s.crewName ? ` • ${s.crewName}` : '';
    const date = s.sessionDate ? ` • ${s.sessionDate}` : '';
    return `${name}${crew}${date}`;
  };
  const formatSessionDate = (dateStr: string, timeStr?: string): string => {
    try {
      // Parse as UTC to avoid timezone issues
      const date = new Date(dateStr + 'T00:00:00.000Z');
      const monthNames = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];

      const month = monthNames[date.getUTCMonth()];
      const day = date.getUTCDate();
      const year = date.getUTCFullYear();

      if (timeStr) {
        // Format time from HH:MM:SS to HH:MM
        const timeParts = timeStr.split(':');
        const hours = timeParts[0];
        const minutes = timeParts[1];
        return `${month} ${day}, ${year} at ${hours}:${minutes}`;
      }

      return `${month} ${day}, ${year}`;
    } catch (error) {
      return dateStr; // Fallback to original string if parsing fails
    }
  };

  const buildMetadata = (): string[] => {
    const metadata: string[] = [];

    // Only add formatted date if we have a real session date
    if (sessionInfo.sessionDate && sessionInfo.sessionDate.trim() !== '') {
      metadata.push(formatSessionDate(sessionInfo.sessionDate, sessionInfo.startTime));
    }

    // Add location if available
    if (sessionInfo.location) {
      metadata.push(sessionInfo.location);
    }

    // Add crew info if available
    if (sessionInfo.crewCount) {
      metadata.push(`${sessionInfo.crewCount}-Person Crew`);
    }

    return metadata;
  };

  const metadataItems = buildMetadata();
  const hasSectionNav = sectionLinks.length > 0 && Boolean(onSelectSection);

  return (
    <header
      className="bg-white py-4 md:px-6 px-2 flex flex-col sm:flex-row border-b border-rowing-gray-200"
      data-testid="compact-header"
    >
      <div
        className={
          hasSectionNav
            ? 'w-full flex flex-col gap-3 xl:grid xl:grid-cols-3 xl:items-center xl:gap-4'
            : 'w-full flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'
        }
      >
        {/* Session Info */}
        <div className={`min-w-0 ${hasSectionNav ? 'xl:col-start-1' : 'sm:flex-1'}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-xl font-semibold text-rowing-navy truncate">
              {sessionInfo.sessionName || 'Session'}
            </h1>
            {sessionInfo.id && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200"
                title={sessionInfo.id}
              >
                {shortenUuid(sessionInfo.id)}
                <button
                  className="ml-1 inline-flex items-center text-gray-500 hover:text-gray-700"
                  onClick={() => navigator.clipboard.writeText(sessionInfo.id || '')}
                  aria-label="Copy full Session ID"
                  title="Copy full Session ID"
                >
                  <Copy size={12} />
                </button>
              </span>
            )}
          </div>
          <div
            className="text-sm text-rowing-gray-600 mt-1 truncate"
            data-testid="session-metadata"
          >
            {metadataItems.join(' | ')}
          </div>
        </div>

        {hasSectionNav && (
          <div className="xl:col-start-2 xl:justify-self-center">
            <div className="text-xs text-content-muted text-left xl:text-center mb-1 flex items-center justify-start xl:justify-center gap-1.5 leading-tight xl:max-w-[420px]">
              <Info
                size={16}
                className="text-content-muted self-center flex-shrink-0"
                aria-hidden="true"
              />
              <span>{sectionLinksHint}</span>
            </div>
            <div className="w-36 h-0.5 bg-border mb-1.5 xl:mx-auto" />
            <div className="flex flex-wrap items-center justify-start xl:justify-center text-xs sm:text-sm">
              {sectionLinks.map((link, index) => (
                <React.Fragment key={link.hash}>
                  {index > 0 && <span className="mx-2 text-content-muted">|</span>}
                  <button
                    type="button"
                    className="ui-link font-medium"
                    onClick={() => onSelectSection(link.hash)}
                  >
                    {link.label}
                  </button>
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        {/* Session switcher (optional) */}
        {!hideSessionSwitcher && onSelectSession && (
          <div
            ref={switcherRef}
            className={`relative flex-shrink-0 w-full ${
              hasSectionNav
                ? 'xl:w-80 xl:col-start-3 xl:justify-self-end'
                : 'sm:w-80 sm:ml-4 sm:flex-none'
            }`}
          >
            <SearchInput
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              onClear={() => setQuery('')}
              placeholder="View another session…"
              wrapperClassName="w-full"
              aria-label="Search sessions"
            />
            {open && filtered.length > 0 && (
              <div
                className="absolute z-20 mt-1 w-full max-h-64 overflow-auto bg-surface border border-border rounded-md shadow-lg"
                onMouseDown={(e) => e.preventDefault()}
              >
                {filtered.map((s) => (
                  <button
                    key={s.id}
                    className="w-full text-left px-3 py-2 text-sm text-content-primary hover:bg-surface-muted"
                    onClick={() => {
                      setQuery('');
                      setOpen(false);
                      onSelectSession && onSelectSession(s.id);
                    }}
                  >
                    {labelFor(s)}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

export default CompactHeader;
